import { format } from 'date-fns';
import { Image } from 'expo-image';
import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Button, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';

const eonOfferSrc = require('../../../../../public/images/eon-offer.png');

interface EonOfferModalProps {
  visible?: boolean;
  onClose: () => void;
  onClick: () => void;
  title?: string;
  body?: string;
  code?: string;
  expiresAt?: string | number | Date | null;
  style?: any;
}

function formatExpiry(expiresAt: EonOfferModalProps['expiresAt']) {
  if (!expiresAt) {
    return null;
  }

  const date = new Date(expiresAt);

  return Number.isNaN(date.getTime()) ? null : format(date, 'MMM d, yyyy');
}

export function EonOfferModal({
  visible = false,
  onClose,
  onClick,
  title,
  body,
  code,
  expiresAt,
  style,
}: EonOfferModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showEonOfferModal, setShowEonOfferModal] = useState(visible);

  useEffect(() => {
    setShowEonOfferModal(visible);
  }, [visible]);

  const expiry = formatExpiry(expiresAt);

  const handleClose = () => {
    setShowEonOfferModal(false);
    onClose?.();
  };

  return (
    <Modal
      animationIn="fadeIn"
      animationInTiming={300}
      animationOut="fadeOut"
      animationOutTiming={300}
      isVisible={showEonOfferModal}
      hasBackdrop
      backdropOpacity={0.8}
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={handleClose}
    >
      <View style={styles.containerView}>
        <View style={styles.hero}>
          <Image
            source={eonOfferSrc}
            contentFit="cover"
            style={styles.heroLayer}
          />

          <Pressable
            hitSlop={8}
            onPress={handleClose}
            style={({ pressed }) => [
              styles.closeButton,
              { opacity: pressed ? 0.5 : 1 },
            ]}
          >
            <Icon
              type="naked"
              icon={<Cancel />}
              stroke={theme.colors.icon.color}
              fillColor={theme.colors.icon.color}
              width={10}
              height={10}
            />
          </Pressable>
        </View>

        <View style={styles.content}>
          <View style={styles.offerSection}>
            {code ? (
              <Chip
                type={ChipType.BRAND}
                text={`CODE: ${code.toUpperCase()}`}
                style={styles.chip}
              />
            ) : null}

            <View style={styles.copyBlock}>
              {title ? (
                <Text type="largeTitle" style={styles.centeredText}>
                  {title}
                </Text>
              ) : null}

              {body ? (
                <Text type="bodyRegular" style={styles.centeredText}>
                  {body}
                </Text>
              ) : null}
            </View>

            {expiry ? (
              <Text type="caption1" style={styles.secondaryText}>
                {`Expires ${expiry}`}
              </Text>
            ) : null}
          </View>

          <Button text="Book A Car" type="primary" onPress={() => onClick()} />
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    justifyContent: 'center',
    alignSelf: 'center',
    paddingHorizontal: {
      xs: 16,
      sm: 0,
    },
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.dialog.backgroundColor,
    borderColor: theme.colors.dialog.borderColor,
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
  },
  hero: {
    width: '100%',
    height: {
      xs: 140,
      md: 170,
    },
  },
  heroLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 30,
    height: 30,
    borderRadius: 100,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background_2,
    borderColor: theme.colors.navigationButton.border,
    borderWidth: 1,
  },
  content: {
    padding: 16,
    gap: 24,
  },
  offerSection: {
    gap: 16,
    width: '100%',
    alignItems: 'center',
  },
  copyBlock: {
    gap: 8,
    width: '100%',
  },
  chip: {
    alignSelf: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  centeredText: {
    textAlign: 'center',
  },
  secondaryText: {
    textAlign: 'center',
    color: theme.colors.text.secondary,
  },
}));
